/** SUNTRONIX AI COPILOT — ask box + linked results + Summarise / Draft / Explain / Analyse / Export / Create task. */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Send, Loader2, ExternalLink, Download, ListChecks, FileText, Lightbulb, BarChart3, PenLine, ShieldAlert } from "lucide-react";

export const EXAMPLES = [
  "Show quotations pending for more than 7 days", "Which customers owe us more than ₹5 lakh?", "Show deliveries delayed this week",
  "Which devices have AppleCare+ expiring in 60 days?", "Show all open orders for Microsoft", "Show devices currently in Suntronix warehouse",
  "Which salesperson has the highest quote conversion?", "Draft a follow-up for STX-Q-2026-00218", "Show gross margin below 5%", "Show all assets belonging to ABC Technologies",
];
const PLACEHOLDER = "Ask anything about customers, quotations, orders, inventory, assets, finance or operations...";

export default function CopilotBox({ compact = false, initialQuestion = "", title = "Suntronix AI Copilot", scopeNote = "" }) {
  const navigate = useNavigate();
  const [q, setQ] = useState(initialQuestion);
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]); // results
  const [sessionId] = useState(() => `cp-${Math.random().toString(36).slice(2, 10)}`);
  const endRef = useRef(null);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (text.length < 3 || busy) return;
    setBusy(true); setQ("");
    try {
      const { data } = await api.post("/copilot/ask", { question: text, session_id: sessionId });
      setThread((t) => [...t, data]);
    } catch (e) { toast.error(e.response?.data?.detail || "Copilot could not answer"); }
    finally { setBusy(false); }
  };
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [thread.length]);
  useEffect(() => { if (initialQuestion) ask(initialQuestion); }, []); // eslint-disable-line

  return (
    <section className={`rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50/70 via-white to-white ${compact ? "p-3" : "p-4 sm:p-5"}`} data-testid="copilot-box">
      <div className="flex items-center gap-2 mb-2">
        <span className="h-7 w-7 rounded-md bg-slate-900 text-sky-300 flex items-center justify-center"><Sparkles className="h-4 w-4" /></span>
        <div>
          <div className="font-heading text-sm font-bold text-slate-900 tracking-tight" data-testid="copilot-title">{title}</div>
          {scopeNote && <div className="text-[10.5px] text-slate-500" data-testid="copilot-scope-note">{scopeNote}</div>}
          {!compact && <div className="text-[11px] text-slate-500">Answers come from live records you are allowed to see, with links to every underlying record.</div>}
        </div>
        {!compact && <button onClick={() => navigate("/copilot")} className="ml-auto text-[11px] text-sky-700 hover:underline" data-testid="copilot-open-full">Open Copilot</button>}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={PLACEHOLDER} className="h-11 text-sm bg-white" data-testid="copilot-input" />
        <Button type="submit" disabled={busy || q.trim().length < 3} className="h-11 px-4" data-testid="copilot-ask">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
      </form>
      {thread.length === 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="copilot-examples">
          {EXAMPLES.slice(0, compact ? 4 : 10).map((ex) => <button key={ex} onClick={() => ask(ex)} className="text-[11px] rounded-full border border-slate-200 bg-white px-2.5 py-1 hover:border-sky-300 hover:bg-sky-50 text-slate-700" data-testid="copilot-example">{ex}</button>)}
        </div>
      )}
      <div className="space-y-3 mt-3">
        {thread.map((r) => <ResultCard key={r.id} r={r} />)}
        {busy && <div className="text-xs text-slate-500 inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking with GPT-5.4…</div>}
        <div ref={endRef} />
      </div>
    </section>
  );
}

function ResultCard({ r }) {
  const navigate = useNavigate();
  const [acts, setActs] = useState({});
  const [running, setRunning] = useState(null);
  const [taskOpen, setTaskOpen] = useState(false);
  const run = async (action) => {
    setRunning(action);
    try { const { data } = await api.post("/copilot/act", { result_id: r.id, action }); setActs((a) => ({ ...a, [action]: data.text })); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setRunning(null); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get(`/copilot/export/${r.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `copilot-${r.tool}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const ACTIONS = [["summarise", "Summarise", ListChecks], ["draft", "Draft Follow-up", PenLine], ["explain", "Explain", Lightbulb], ["analyse", "Analyse", BarChart3]];
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`copilot-result-${r.id}`}>
      <div className="text-[11px] text-slate-400 font-mono">You asked · {r.question}</div>
      <div className="text-sm text-slate-900 mt-1 whitespace-pre-wrap" data-testid="copilot-summary">{r.summary}</div>
      {r.restricted_hint && <div className="mt-1 text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid="copilot-restricted"><ShieldAlert className="h-3 w-3" />{r.restricted_hint}</div>}
      {r.rows?.length > 0 && (
        <div className="overflow-x-auto mt-2 -mx-1">
          <table className="w-full text-[12px] min-w-[520px]" data-testid="copilot-table">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">{r.columns.map((c) => <th key={c} className="px-2 py-1 font-semibold">{c}</th>)}<th /></tr></thead>
            <tbody>
              {r.rows.slice(0, 25).map((row, i) => (
                <tr key={i} onClick={() => row.route && navigate(row.route)} className={`border-t border-slate-100 ${row.route ? "cursor-pointer hover:bg-sky-50/50" : ""}`} data-testid={`copilot-row-${i}`}>
                  {r.columns.map((c) => <td key={c} className="px-2 py-1.5 align-top">{String(row[c] ?? "—")}</td>)}
                  <td className="px-2 py-1.5 text-slate-300">{row.route && <ExternalLink className="h-3 w-3" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {r.row_count > 25 && <div className="text-[11px] text-slate-400 px-2 pt-1">Showing 25 of {r.row_count} — use Export for the full list.</div>}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 mt-3" data-testid="copilot-actions">
        {ACTIONS.map(([k, label, Icon]) => <button key={k} onClick={() => run(k)} disabled={!!running} className="h-7 px-2.5 rounded border border-slate-200 text-[11px] inline-flex items-center gap-1 hover:bg-slate-50 disabled:opacity-50" data-testid={`copilot-act-${k}`}>{running === k ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}{label}</button>)}
        {r.rows?.length > 0 && <button onClick={exportCsv} className="h-7 px-2.5 rounded border border-slate-200 text-[11px] inline-flex items-center gap-1 hover:bg-slate-50" data-testid="copilot-export"><Download className="h-3 w-3" />Export</button>}
        <button onClick={() => setTaskOpen(true)} className="h-7 px-2.5 rounded bg-slate-900 text-white text-[11px] inline-flex items-center gap-1 hover:bg-slate-800" data-testid="copilot-create-task"><FileText className="h-3 w-3" />Create Task</button>
      </div>
      {Object.entries(acts).map(([k, text]) => <div key={k} className="mt-2 rounded-md bg-slate-50 border border-slate-100 px-3 py-2 text-[13px] text-slate-800 whitespace-pre-wrap" data-testid={`copilot-act-result-${k}`}><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{k}</div>{text}</div>)}
      <TaskDialog open={taskOpen} onClose={() => setTaskOpen(false)} r={r} />
    </div>
  );
}

function TaskDialog({ open, onClose, r }) {
  const [f, setF] = useState({ title: `Follow up: ${r.question}`.slice(0, 120), assignee_email: "", due_at: "", priority: "medium" });
  const [preview, setPreview] = useState(null);
  const [users, setUsers] = useState([]);
  useEffect(() => { if (open) api.get("/one-today/assignees").then(({ data }) => setUsers(data.items || [])).catch(() => {}); }, [open]);
  const submit = async (confirm) => {
    try {
      const { data } = await api.post("/copilot/task", { result_id: r.id, ...f, assignee_email: f.assignee_email || null, due_at: f.due_at || null, confirm });
      if (data.requires_confirmation) { setPreview(data.preview); return; }
      toast.success("Task created — it now appears in My Action Centre"); onClose(); setPreview(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create task"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="copilot-task-dialog">
        <DialogHeader><DialogTitle className="text-base">Create task from Copilot</DialogTitle></DialogHeader>
        {!preview ? (
          <div className="space-y-2 text-sm">
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="copilot-task-title" />
            <select value={f.assignee_email} onChange={(e) => setF({ ...f, assignee_email: e.target.value })} className="w-full h-9 rounded-md border border-slate-200 px-2 bg-white" data-testid="copilot-task-assignee">
              <option value="">Assign to me</option>{users.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
            </select>
            <div className="flex gap-2">
              <input type="date" value={f.due_at} onChange={(e) => setF({ ...f, due_at: e.target.value })} className="h-9 rounded-md border border-slate-200 px-2 flex-1" data-testid="copilot-task-due" />
              <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="h-9 rounded-md border border-slate-200 px-2 bg-white" data-testid="copilot-task-priority">{["critical", "high", "medium", "low"].map((p) => <option key={p}>{p}</option>)}</select>
            </div>
            <Button className="w-full" onClick={() => submit(false)} disabled={!f.title.trim()} data-testid="copilot-task-review">Review</Button>
          </div>
        ) : (
          <div className="space-y-3 text-sm" data-testid="copilot-task-confirm">
            <p className="text-slate-600">Copilot will create this task. Nothing else will be changed.</p>
            <div className="rounded-md bg-slate-50 border border-slate-100 p-3 text-[13px]"><b>{preview.title}</b><div className="text-slate-500 mt-1">Assignee {preview.assignee} · Priority {preview.priority} · Due {preview.due_at || "—"}</div></div>
            <div className="flex gap-2"><Button variant="outline" onClick={() => setPreview(null)} data-testid="copilot-task-back">Back</Button><Button onClick={() => submit(true)} data-testid="copilot-task-confirm-btn">Confirm & create</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
